/**
 *  Cart
 *    Functions to handle all cart events.
 */

import {
  changeCart,
  cartAdd,
  ON_CART_FINISHED,
  removeFromCart,
  getCurrentCart,
} from '@process-creative/slate-theme-tools';

import { t } from '../../tools/utilities/string-tools';
import { shopifyAjaxErrorGet } from '../../tools/utilities/error';
import {
  CLASS_SCROLL_DISABLED,
  CLASS_DRAWER_OPEN,
  CLASS_CART_PENDING,
  CLASS_VISIBLE
} from '../../tools/constants/classes';
import { findTarget } from '../../tools/utilities/event-tools';

import { checkFreeGifts } from './cart-free-gifts';
import '../subscription-selector/subscription-selector';
import { trapFocus, trapFocusDelay, removeTrapFocus } from '../../tools/utilities/trap-focus';

// Selectors
const SELECTOR_CONTAINER = '[data-cart-container]';
const SELECTOR_PRODUCT = '[data-cart-product]';
const SELECTOR_OPEN = '[data-cart-drawer-open]';
const SELECTOR_CLOSE = '[data-cart-drawer-close]';
const SELECTOR_OVERLAY = '[data-cart-drawer-overlay]';
const SELECTOR_SHOPIFY_SECTION = '#shopify-section-cart-contents';
const SELECTOR_SUGGESTION_ADD = '[data-cart-suggestion-add-btn]';
const SELECTOR_SUGGESTION_PRODUCT = '[data-cart-suggestion-product]';
const SELECTOR_REMOVE_BTN = '[data-cart-remove-btn]';
const SELECTOR_QTY_INPUT = '[data-quantity-selector-field]';
const SELECTOR_QTY_PLUS = '[data-quantity-selector-plus]';
const SELECTOR_QTY_MINUS = '[data-quantity-selector-minus]';
const SELECTOR_LINE_ITEM_JSON = '[data-cart-item-json]';
const SELECTOR_CART_COUNT = '[data-cart-count]';

// Attributes
const ATTR_VARIANT_ID = 'data-variant-id';
const ATTR_LINE_ITEM_INDEX = 'data-cart-remove';
const ATTR_PRODUCT_ID = 'data-cart-product';
const ATTR_SECTION_ID = 'data-section-id';

const body = document.body!;

document
  .querySelectorAll<HTMLDivElement>(SELECTOR_CONTAINER)
  .forEach(async (container) => {
    let cartOpen = false;

    /**
     *  Elements
     */
    let openBtn: HTMLButtonElement;
    let closeBtn: HTMLButtonElement;
    let overlay: HTMLElement;
    let suggestionAddBtns: NodeListOf<HTMLButtonElement>;
    let removeButtons: NodeListOf<HTMLButtonElement>;
    let qtyPlusBtns: NodeListOf<HTMLElement>;
    let qtyMinusBtns: NodeListOf<HTMLElement>;

    removeFreeGiftIfUnavailableGWP();

    checkUrl();
    addEventListeners();

    document.addEventListener(ON_CART_FINISHED, () => onCartFinished());
    document.addEventListener('refreshCart', () => renderCartContents());

    function addEventListeners() {
      refreshElements();

      openBtn.addEventListener('click', onCartToggle);
      closeBtn.addEventListener('click', onCartToggle);
      overlay.addEventListener('click', onCartToggle);

      container.addEventListener('keydown', closeCartOnEsc);

      suggestionAddBtns.forEach((btn) =>
        btn.addEventListener('click', onSuggestionAdd)
      );
      removeButtons.forEach((btn) =>
        btn.addEventListener('click', onItemRemove)
      );

      qtyPlusBtns.forEach((btn) => btn.addEventListener('click', onQtyPlus));
      qtyMinusBtns.forEach((btn) => btn.addEventListener('click', onQtyMinus));
    }

    function removeEventListeners() {
      if (openBtn)
        openBtn.removeEventListener('click', onCartToggle);
      if (closeBtn)
        closeBtn.removeEventListener('click', onCartToggle);
      if (overlay)
        overlay.removeEventListener('click', onCartToggle);
      if (container)
        container.removeEventListener('keydown', closeCartOnEsc);

      if (suggestionAddBtns)
        suggestionAddBtns.forEach((btn) =>
          btn.removeEventListener('click', onSuggestionAdd)
        );
      if (removeButtons)
        removeButtons.forEach((btn) =>
          btn.removeEventListener('click', onItemRemove)
        );
      if (qtyPlusBtns)
        qtyPlusBtns.forEach((btn) =>
          btn.removeEventListener('click', onQtyPlus)
        );
      if (qtyMinusBtns)
        qtyMinusBtns.forEach((btn) =>
          btn.removeEventListener('click', onQtyMinus)
        );
    }

    /**
     * Assigns variables
     */
    function refreshElements() {
      openBtn = document.querySelector(SELECTOR_OPEN);
      closeBtn = document.querySelector(SELECTOR_CLOSE);
      overlay = document.querySelector(SELECTOR_OVERLAY);
      suggestionAddBtns = container.querySelectorAll(SELECTOR_SUGGESTION_ADD);
      removeButtons = container.querySelectorAll(SELECTOR_REMOVE_BTN);
      qtyPlusBtns = container.querySelectorAll(SELECTOR_QTY_PLUS);
      qtyMinusBtns = container.querySelectorAll(SELECTOR_QTY_MINUS);
    }

    /**
     * Calls function to render cart
     * Opens the cart if it is not open
     */
    function onCartFinished() {
      renderCartContents();

      // open cart if it's not open
      if (!cartOpen) setTimeout(() => openCart(), 500);      

      // if cart is open, trap focus on the close button after cart finishes updating
      setTimeout(() => {
        if (cartOpen) trapFocus(container, closeBtn, true);
      }, 800);
    }

    /**
     * Updates cart count in header
     */
    function updateCartCount() {
      const countElement =
        document.querySelector<HTMLElement>(SELECTOR_CART_COUNT);
      const count = getCurrentCart().item_count;

      if (!countElement) return;

      countElement.innerHTML = `${count}`;
      countElement.classList.toggle(CLASS_VISIBLE, count > 0);
    }

    /**
     * Toggles open/close state of cart drawer
     * @param evt click event
     */
    function onCartToggle(evt: Event) {
      evt.preventDefault();

      cartOpen 
        ? closeCart()
        : openCart();
    }

    /**
     * Adds suggestion product to cart
     * Updates button text
     *
     * @param evt Click event
     */
    async function onSuggestionAdd(evt: Event) {
      const button = findTarget(evt, SELECTOR_SUGGESTION_ADD);
      const suggestionProduct = button?.closest<HTMLElement>(
        SELECTOR_SUGGESTION_PRODUCT
      );
      if (!suggestionProduct) return;
      const sellingPlanInput = suggestionProduct.querySelector<HTMLInputElement>('[data-subscription-selector-selling-plan-id]');
      const variantID = suggestionProduct.getAttribute(ATTR_VARIANT_ID);
      if (!variantID)
        throw new Error(
          'Invalid markup detected in the product suggestion snippet.'
        );

      try {
        if (button) button.innerText = t('general.cart.adding');
        await cartAdd({
          items: [
            {
              id: parseInt(variantID, 10),
              quantity: 1,
              selling_plan: sellingPlanInput ? sellingPlanInput.value : null,
            },
          ],
        });
        await checkFreeGifts(parseInt(variantID, 10));
        onCartFinished();
      } catch (err) {
        // Error, show error, enable buttons hide loader
        console.error('Failed to add suggestion to cart.');
      }
    }

    /**
     * Removes item from cart based on the item's line index
     * @param evt Click event
     */
    async function onItemRemove(evt: Event) {
      evt.preventDefault();
      body.classList.add(CLASS_CART_PENDING);
      const button = evt.currentTarget as HTMLElement;
      const lineindex = button.getAttribute(ATTR_LINE_ITEM_INDEX);
      if (!lineindex) return;
      const li = parseInt(lineindex, 10);

      const parent = findTarget(evt, SELECTOR_PRODUCT);
      if (!parent) return;

      const jsonElem = parent.querySelector<HTMLScriptElement>(
        SELECTOR_LINE_ITEM_JSON
      );
      if (!jsonElem) return;

      const variantId = parent.getAttribute(ATTR_PRODUCT_ID);
      if (!variantId) return;

      try {
        await removeFromCart(li);
        onCartFinished();
      } catch (err) {
        console.error(shopifyAjaxErrorGet(err));
      }
      setTimeout(() => body.classList.remove(CLASS_CART_PENDING), 300);
    }

    /**
     *
     * @param evt click event
     * @param change string that determines if you're adding or subtracting. Must be add or subtract
     * Get the index of the cart item clicked on, then adds or removes 1 from the quantity depending on argument passed in
     */
    async function onQtyChange(evt: Event, change: 'add' | 'subtract') {
      const target = evt.currentTarget as HTMLElement;
      const parent = target.closest(SELECTOR_PRODUCT);
      let quantity;
      if (!parent) return;

      const variant = parent.getAttribute('data-cart-product');
      const key = parent.getAttribute('data-cart-product-key');
      const inputQuantity =
        parent.querySelector<HTMLInputElement>(SELECTOR_QTY_INPUT);
      if (!variant || !inputQuantity) return;

      if (change === 'add') {
        quantity = parseInt(inputQuantity.value, 10) + 1;
      } else if (change === 'subtract') {
        quantity = parseInt(inputQuantity.value, 10) - 1;
      }

      if (quantity !== undefined) {
        await changeCart({ key, quantity });
        onCartFinished();
      }
    }

    function onQtyPlus(evt: Event) {
      onQtyChange(evt, 'add');
    }

    function onQtyMinus(evt: Event) {
      onQtyChange(evt, 'subtract');
    }

    /**
     * Adds a class to open cart drawer
     * Adds class to body to disable scrolling
     */
    function openCart() {
      container.classList.add(CLASS_DRAWER_OPEN);
      body.classList.add(CLASS_SCROLL_DISABLED);
      cartOpen = true;

      container.addEventListener('transitionend', (e) => {
        trapFocus(container, closeBtn, true);
      }, { once: true });
    }

    /**
     * Removes a class to close cart drawer
     * Removes class to body to enable scrolling
     */
    function closeCart() {
      container.classList.remove(CLASS_DRAWER_OPEN);
      body.classList.remove(CLASS_SCROLL_DISABLED);
      cartOpen = false;

      container.addEventListener('transitionend', (e) => {
        removeTrapFocus(openBtn, true);
      }, { once: true });
    }

    /**
     * Closes cart when escape key is pressed
     * @param evt: KeyboardEvent
     */
    function closeCartOnEsc(evt: KeyboardEvent) {
      if (evt.key === 'Escape' || evt.key === 'Esc') {
        if (cartOpen) {
          closeCart();
        }
      }
    }

    /**
     * Check URL and open cart if URL has ?cart in it. 
     */
    function checkUrl(): void {
      const searchParams = new URLSearchParams(window.location.search);

      if (!searchParams.has('cart')) return;

      searchParams.delete('cart');
      openCart();
      const deletePathName = searchParams.toString().length ? `?${searchParams.toString()}` : '' ;
      const newUrl = `${window.location.origin}/${deletePathName}`;
      window.history.pushState(null, '', newUrl);
    }

    /**
     *  renderCartContents
     *    Uses the Section Rendering API to render the contents of the cart
     *    drawer and print them in the container for the cart drawer
     */
    async function renderCartContents() {
      // Set global pending state
      body.classList.add(CLASS_CART_PENDING);

      // Remove event listeners
      removeEventListeners();

      const sectionID = container.getAttribute(ATTR_SECTION_ID);
      if (!sectionID)
        throw new Error('Invalid markup detected in the Cart Drawer section. Invalid Section ID.');

      const sectionHtml: string = await fetch(`/?sections=${sectionID}`)
        .then((res) => {
          return res.json();
        })
        .then((res) => res[`${sectionID}`]);

      const html = document.createElement('div');
      html.innerHTML = sectionHtml;
      const sectionWrapper = html.querySelector<HTMLDivElement>(
        SELECTOR_CONTAINER
      );

      if (!sectionWrapper)
        throw new Error('Invalid markup detected in the Cart Drawer section.');

      container.innerHTML = String(sectionWrapper.innerHTML);

      // DOM changed, refresh listeners
      addEventListeners();

      // Remove global pending state
      body.classList.remove(CLASS_CART_PENDING);

      // Validate free gifts
      validateFreeGifts();

      // Update cart count in header
      updateCartCount();
    }
    /**
     *  validateFreeGifts
     *    Validate that our free gift items are discounted to 0, if not remove them from the cart.
     */
    function validateFreeGifts() {
      const freeGifts = document.querySelectorAll(
        '[data-cart-product-free-gift]'
      );

      freeGifts.forEach((gift) => {
        const giftPrice = gift.getAttribute(
          'data-cart-product-free-gift-price'
        );
        const giftPriceInt = parseInt(giftPrice, 10);
        const removeButton = gift.querySelector<HTMLButtonElement>('[data-cart-remove]');

        if (giftPriceInt === 0 || !removeButton) {
          return;
        }

        removeButton.click();
      });
    }

    function removeFreeGiftIfUnavailableGWP() {
      const freeGiftsData = document.querySelectorAll(
        '[data-cart-product-free-gift]'
      );
      const freeGiftChecker = document.querySelector('[data-available-gwp]');
      const value = freeGiftChecker?.getAttribute('data-available-gwp');

      if (freeGiftsData && value === 'false') {
        freeGiftsData.forEach((gift) => {
          const removeButton = gift.querySelector<HTMLButtonElement>('[data-cart-remove]');

          if (!removeButton) {
            return;
          }

          removeButton.click();
        });
      }
    }
  });
